/* eslint-disable max-len */
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { FormArray, ReactiveFormsModule } from '@angular/forms';
import { ScoreDetailService } from '@core/score-detail.service';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { AssessmentStatus } from '@shared/branded-reports/branded-individual-student-over-time-report-detail/student-over-time-report-detail.model';
import { ButtonComponent } from '@shared/button/button.component';
import { ProductType } from '@shared/enums/product-type';
import { ScoreBenchmark } from '@shared/enums/score-benchmark';
import { TopicType } from '@shared/enums/topic-type';
import { GenericConfirmModalComponent } from '@shared/modals/generic-confirm-modal/generic-confirm-modal.component';
import { ModalTemplateComponent } from '@shared/modals/modal-template/modal-template.component';
import { PreloaderModule } from '@shared/preloader/preloader.module';
import { ScoreDetailViewComponent } from '@shared/score-detail/score-detail-view/score-detail-view.component';
import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { catchError, firstValueFrom, from, Observable, of, tap } from 'rxjs';

import { IScoreDetailActivityModalParams, IScoreDetailAssessmentModalParams } from './score-detail-params';
/* eslint-enable max-len */

@Component({
  selector: 'zbp-score-detail-modal',
  templateUrl: './score-detail-modal.component.html',
  styleUrls: ['./score-detail-modal.component.scss'],
  imports: [
    CdkTrapFocus,
    ButtonComponent,
    ReactiveFormsModule,
    CommonModule,
    ModalTemplateComponent,
    PreloaderModule,
    ScoreDetailViewComponent,
    PreloaderModule,
    TranslateModule
  ]
})
export class ScoreDetailModalComponent implements AfterViewInit, OnDestroy {
  // Common variables
  studentName: string = '';
  className: string = '';
  productName: string = '';
  activityName: string = '';
  score: string = '';
  topicType: TopicType;
  timeSpent?: string;

  // Activity variables
  /**
   * This is used for student activity reports.
   *
   * @todo provide the union type, ScoreDetail, and then change conditionals to check based on the specific class
   *       instead of relying on "any" type. ZBP-1872.
   */
  scoreDetail: any;
  benchmark: ScoreBenchmark;

  // Assessment variables
  /**
   * This is used for progress reports.
   *
   * Note that the term "part" is not the same model as described in score.ts.
   *
   * @todo provide the correct type for this rather than "any" type. ZBP-1872.
   */
  parts: any[] | undefined;
  productType: ProductType;
  // @todo determine if VariantType or string is the correct type instead of "any". ZBP-1872.
  variant: any;
  classroomId: string;
  studentId: string;
  assessmentKey: string;
  /**
   * Whether manual scoring should be used on initial load.
   *
   * This is described as whether the score has already been overridden or the status is AddScore.
   */
  manualScore = false;
  scoreIsOverridden = false;
  assessmentStatus: AssessmentStatus;

  columns: ZbpDataTable.Column[] = [];
  rows: ZbpDataTable.Row[] = [];

  zbpIconNames = zbpIconNames;

  addSubFormControls = new FormArray([]);

  manualScoreResponse: any;

  allowEdit: boolean;

  /** InProgress and NotStarted cannot be edited */
  editableStatuses = [AssessmentStatus.Completed, AssessmentStatus.AddScore, AssessmentStatus.PartiallyScored];

  isSaving = false;
  isEditing = false;
  hasUnscoredManualSection = false;
  isLoading = false;

  // Printing
  /** Headers that are sent to the printable header component. */
  printableHeaders: IPrintableReportHeading[] = [];

  @ViewChild(ScoreDetailViewComponent) scoreDetailView?: ScoreDetailViewComponent;

  constructor(
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private scoreDetailService: ScoreDetailService,
  ) { }

  ngAfterViewInit(): void {
    // This needs to be added and removed manually because afterprint also exists on branded progress reports
    window.addEventListener('afterprint', this.afterPrint);
  }

  ngOnDestroy(): void {
    window.removeEventListener('afterprint', this.afterPrint);
  }

  // This needs to be a property to keep context
  afterPrint = () => {
    // Restore original states using the data table's method
    this.scoreDetailView?.restoreCollapsibleRowStates();
  };

  public static async openActivity(ngbModal: NgbModal, params: IScoreDetailActivityModalParams): Promise<NgbModalRef> {
    let component: ScoreDetailModalComponent;

    const modalRef = ngbModal.open(
      ScoreDetailModalComponent,
      {
        ...params?.options,
        size: 'xl',
        scrollable: true,
        beforeDismiss: () => (component?.hasUnsavedChanges()
          ? component?.openConfirmModal(ngbModal)
          : true),
      },
    );

    // Common
    component = modalRef.componentInstance as ScoreDetailModalComponent;
    component.studentName = params.studentName;
    component.className = params.className;
    component.productName = params.productName;
    component.activityName = params.activityName;
    component.score = params.score;
    component.topicType = params.topicType;
    component.timeSpent = params.timeSpent;

    // Activity
    component.scoreDetail = params.scoreDetail;
    component.benchmark = component.scoreDetail.benchmark;

    component.buildTable();
    return modalRef;
  }

  public static async openAssessment(ngbModal: NgbModal, params: IScoreDetailAssessmentModalParams): Promise<NgbModalRef> {
    let component: ScoreDetailModalComponent;

    const modalRef = ngbModal.open(
      ScoreDetailModalComponent,
      {
        ...params?.options,
        size: 'xl',
        scrollable: true,
        beforeDismiss: () => (component?.hasUnsavedChanges()
          ? component?.openConfirmModal(ngbModal)
          : true),
      },
    );

    // Common
    component = modalRef.componentInstance as ScoreDetailModalComponent;
    component.studentName = params.studentName;
    component.className = params.className;
    component.productName = params.productName;
    component.activityName = params.activityName;
    component.score = params.score;
    component.topicType = params.topicType;

    component.parts = params.parts;
    component.benchmark = params.benchmark;
    component.productType = params.productType;
    component.variant = params.variant;
    component.classroomId = params.classroomId;
    component.studentId = params.studentId;
    component.assessmentKey = params.assessmentKey;
    component.assessmentStatus = params.assessmentStatus;
    component.scoreIsOverridden = params.scoreIsOverridden;
    component.manualScore = params.scoreIsOverridden
      || params.assessmentStatus === AssessmentStatus.AddScore
      || params.assessmentStatus === AssessmentStatus.PartiallyScored;

    if (params.assessmentStatus === AssessmentStatus.AddScore) {
      component.isEditing = true;
    }

    if (component.manualScore) {
      component.isLoading = true;
      await firstValueFrom(component.getAssessmentManualScore());
      component.isLoading = false;
    }

    component.allowEdit = params.allowEdit;

    component.buildTable();
    return modalRef;
  }

  buildTable(): void {
    const vm = this.scoreDetailService.buildTable({
      topicType: this.topicType,
      score: this.score,
      benchmark: this.benchmark,
      scoreDetail: this.scoreDetail,
      parts: this.parts,
      manualScoreResponse: this.manualScoreResponse,
      isEditing: this.isEditing,
      productType: this.productType,
      timeSpent: this.timeSpent,
    });

    this.columns = vm.columns;
    this.rows = vm.rows;
    this.hasUnscoredManualSection = vm.hasUnscoredManualSection;

    // Populate addSubFormControls with form groups created by the service
    if (this.parts && vm.formGroups) {
      this.addSubFormControls.clear();
      vm.formGroups.forEach((group) => {
        this.addSubFormControls.push(group);
      });
    }
  }

  hasUnsavedChanges(): boolean {
    return this.addSubFormControls.dirty;
  }

  openConfirmModal(ngbModal: NgbModal): Promise<any> {
    const confirmCloseModalRef = GenericConfirmModalComponent.open(ngbModal, {
      title: '',
      body: 'You have unsaved changes, please click confirm to continue.',
    });
    return confirmCloseModalRef.result;
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    const dirtyFields = this.addSubFormControls.controls.filter(item => item.dirty);

    const values = dirtyFields.map((value) => {
      const correctValue = value.value;
      if (correctValue.totalCorrect === null) {
        return { part: correctValue.part, deleteScore: true };
      }

      return correctValue;
    });

    if (values.length > 0) {
      this.setManualScore(values).subscribe((result) => {
        if (result.success) {
          this.activeModal.close({ refreshData: true });
        }
      });
    }
  }

  enableEditMode(): void {
    // Displays a confirmation modal informing teachers of potential loss of prompts.
    const confirmModalRef = GenericConfirmModalComponent.open(this.modalService, {
      title: 'Are you sure you want to proceed?',
      body: `Manually overriding this students test score will remove all previously
        recorded prompt information from the impacted parts of their online test.
        This action is irreversible.  Do you want to continue?`,
      confirm: { label: 'Confirm' },
      options: { size: 'med' }
    });

    const confirm$ = from(confirmModalRef.result).pipe(
      catchError(() => of(false))
    );

    confirm$.subscribe((response) => {
      const userConfirmed = response?.confirm === true;
      if (!userConfirmed) {
        return;
      }

      this.isEditing = true;
      if (!this.manualScoreResponse) {
        // Need to wait for observable
        this.getAssessmentManualScore().subscribe(() => {
          this.buildTable();
        });
      } else {
        this.buildTable();
      }
    });
  }

  getAssessmentManualScore(): Observable<any> {
    this.isSaving = true;
    return this.scoreDetailService.getAssessmentManualScore({
      assessmentKey: this.assessmentKey,
      productType: this.productType,
      variant: this.variant,
      classroomId: this.classroomId,
      studentId: this.studentId,
    }).pipe(
      tap((response) => {
        this.manualScoreResponse = response;
        this.isSaving = false;
      }),
    );
  }

  private setManualScore(data: { part: string; totalCorrect: number }[]): Observable<any> {
    this.isSaving = true;
    return this.scoreDetailService.setManualScore({
      assessmentKey: this.assessmentKey,
      productType: this.productType,
      variant: this.variant,
      classroomId: this.classroomId,
      studentId: this.studentId,
      data,
    }).pipe(
      tap(() => {
        this.isSaving = false;
      }),
    );
  }

  print(): void {
    this.getPrintHeaders();
    if (this.scoreDetailView?.activityName?.toLocaleLowerCase().includes('fluency')) {
      this.scoreDetailView?.collapseAllCollapsibleRows();
    } else {
      this.scoreDetailView?.expandAllCollapsibleRows();
    }

    // Wait for DOM updates and then print
    setTimeout(() => {
      window.print();
    }, 100);
  }

  private getPrintHeaders(): void {
    this.printableHeaders = [
      { label: 'Student', value: this.studentName ?? '' },
      { label: 'Class', value: this.className ?? '' },
      { label: 'Report', value: 'Score Detail' },
      { label: 'Product', value: this.productName ?? 'All Classes' },
      { label: 'Activity', value: this.activityName ?? '' },
    ];
  }
}
